/**
 * Host clue-rating contract (#201, FR-11.x quality).
 *
 * A host, when the game-creation "Rate clues" option is enabled, may rate a completed
 * clue **good/bad** (with optional "why" reason chips on `bad`) from a panel below the
 * board. A rating is a **private, host-attributed annotation** — it is NEVER part of the
 * game state or the WebSocket command/event contract (D201.2/D201.12): it rides the HTTP
 * control plane (`POST /api/clue-ratings`), so it carries no {@link PROTOCOL_VERSION}
 * weight. This module is the single source of truth for the payload shape + validation so
 * the game-server route (the authority) and the web host client (inline pre-validation)
 * can never drift (AGENT_GUIDE rule 2), mirroring how the suggestions / data-rights DTOs
 * live here.
 */

/** A host's verdict on a completed clue's quality (D201.10). */
export type RatingVerdict = 'good' | 'bad';

/** The verdict values, for exhaustive validation + UI enumeration. */
export const RATING_VERDICTS = ['good', 'bad'] as const;

/**
 * The fixed set of "why is this clue bad?" reason chips shown on a 👎 (D201.10, mock 08).
 * A closed set keeps ground truth measurable by the eval harness (D201.13) — a bare thumb
 * is not. `self-revealing` maps directly to the FR-11.1 detector the eval scores against.
 */
export const RATING_REASONS = [
  'self-revealing',
  'ambiguous',
  'wrong-answer',
  'too-easy',
  'too-hard',
  'other',
] as const;

/** One allowed reason chip. */
export type RatingReason = (typeof RATING_REASONS)[number];

/**
 * The single **reserved synthetic clue** every automated test that submits a rating MUST
 * rate (#249, NFR-6.11). It is the shared convention that keeps test-created ratings out of
 * the committed ground-truth corpus: deployed e2e / any future rating-creating test imports
 * this exact triple, and the collation CLI drops any pulled rating whose `(category, prompt)`
 * matches it (compared by the same content hash the store keys on). Keep the text
 * deliberately distinctive so a real host is astronomically unlikely to author the identical
 * clue (and be wrongly excluded). This is a plain data constant — it carries no
 * {@link PROTOCOL_VERSION} weight and rides no wire.
 */
export const SYNTHETIC_TEST_CLUE = {
  category: 'Synthetic Test Clue (#249) — Snake Facts & Endangered Species',
  prompt: 'These predators swallow prey whole. [automated-test fixture — not real GT]',
  response: 'Snake',
} as const;

/** Max characters accepted for a rated clue's category snapshot (size cap before store). */
export const MAX_RATING_CATEGORY_LEN = 200;
/** Max characters accepted for a rated clue's prompt snapshot. */
export const MAX_RATING_PROMPT_LEN = 2000;
/** Max characters accepted for a rated clue's response snapshot. */
export const MAX_RATING_RESPONSE_LEN = 500;
/** Max characters accepted for the `gameId` the rating is scoped to. */
export const MAX_RATING_GAME_ID_LEN = 200;

/**
 * The client→server body of `POST /api/clue-ratings`. Carries the clue snapshot the host
 * just played (category/prompt/response — the host client already holds it), the verdict,
 * optional reason chips, the scoping `gameId`, and the host `ticket` proving host authority
 * for that game (verified server-side; the rater's identity is resolved from the game's
 * authoritative host, never client-trusted — D201.3).
 */
export interface ClueRatingSubmission {
  /** The game the clue belongs to (host authority is scoped to it). */
  gameId: string;
  /** The rated clue's category name (snapshot). */
  category: string;
  /** The rated clue's prompt / clue text (snapshot). */
  prompt: string;
  /** The rated clue's accepted response (snapshot). */
  response: string;
  /** The host's verdict. */
  verdict: RatingVerdict;
  /** Optional reason chips — only meaningful for a `bad` verdict. */
  reasons?: RatingReason[];
  /** The host ticket authorizing the rating for `gameId` (verified server-side). */
  ticket: string;
}

/**
 * A validated + normalized clue rating: content fields trimmed, `reasons` deduped and
 * present only for a `bad` verdict. This is what the store persists as ground truth; the
 * transport-only `ticket` is intentionally absent (it is consumed by the auth check, not
 * stored).
 */
export interface NormalizedClueRating {
  gameId: string;
  category: string;
  prompt: string;
  response: string;
  verdict: RatingVerdict;
  /** Present (possibly empty) only for a `bad` verdict; omitted for `good`. */
  reasons?: RatingReason[];
}

/** The result of validating a clue-rating submission. */
export type ClueRatingValidation =
  { ok: true; value: NormalizedClueRating } | { ok: false; error: string };

/**
 * The client→server body of `DELETE /api/clue-ratings` — a host **retracting** ("Undo") a
 * rating they just recorded, within the brief post-save window (#201, FR-11.x). It carries
 * only the (gameId, category, prompt) snapshot that KEYS the row plus the host `ticket`; the
 * rater identity is resolved server-side from the signed guest token, so a host can retract
 * only their OWN row (D201.3). No verdict/response/reasons — a retract removes the whole row.
 */
export interface ClueRatingRetraction {
  /** The game the clue belongs to (host authority is scoped to it). */
  gameId: string;
  /** The rated clue's category name — half of the per-clue key. */
  category: string;
  /** The rated clue's prompt / clue text — the other half of the per-clue key. */
  prompt: string;
  /** The host ticket authorizing the retraction for `gameId` (verified server-side). */
  ticket: string;
}

/** A validated + normalized retraction: the (gameId, category, prompt) key trimmed. */
export interface NormalizedClueRatingRetraction {
  gameId: string;
  category: string;
  prompt: string;
}

/** The result of validating a clue-rating retraction. */
export type ClueRatingRetractionValidation =
  { ok: true; value: NormalizedClueRatingRetraction } | { ok: false; error: string };

function isRatingVerdict(v: unknown): v is RatingVerdict {
  return typeof v === 'string' && (RATING_VERDICTS as readonly string[]).includes(v);
}

function isRatingReason(v: unknown): v is RatingReason {
  return typeof v === 'string' && (RATING_REASONS as readonly string[]).includes(v);
}

/**
 * Validate + normalize an untrusted clue-rating submission. The single authority for the
 * rating content shape — reused by the web host client (inline, before POST) and by the
 * game-server route (the trust boundary). Rejects a missing/oversize snapshot, an unknown
 * verdict, or an unknown reason; drops reasons on a `good` verdict (chips are the "why
 * bad?" affordance only). The `ticket` and `gameId` are NOT this function's concern beyond
 * a shape/size check — host authority is enforced server-side against the game's host.
 */
export function validateClueRating(input: unknown): ClueRatingValidation {
  if (typeof input !== 'object' || input === null) {
    return { ok: false, error: 'rating must be an object' };
  }
  const body = input as Record<string, unknown>;

  const verdict = body.verdict;
  if (!isRatingVerdict(verdict)) {
    return { ok: false, error: 'verdict must be "good" or "bad"' };
  }

  const gameId = typeof body.gameId === 'string' ? body.gameId.trim() : '';
  if (gameId.length === 0) return { ok: false, error: 'gameId is required' };
  if (gameId.length > MAX_RATING_GAME_ID_LEN) {
    return { ok: false, error: 'gameId is too long' };
  }

  const category = typeof body.category === 'string' ? body.category.trim() : '';
  const prompt = typeof body.prompt === 'string' ? body.prompt.trim() : '';
  const response = typeof body.response === 'string' ? body.response.trim() : '';
  if (category.length === 0 || prompt.length === 0 || response.length === 0) {
    return { ok: false, error: 'category, prompt, and response are required' };
  }
  if (category.length > MAX_RATING_CATEGORY_LEN) {
    return { ok: false, error: 'category is too long' };
  }
  if (prompt.length > MAX_RATING_PROMPT_LEN) {
    return { ok: false, error: 'prompt is too long' };
  }
  if (response.length > MAX_RATING_RESPONSE_LEN) {
    return { ok: false, error: 'response is too long' };
  }

  let reasons: RatingReason[] | undefined;
  if (verdict === 'bad' && body.reasons !== undefined) {
    if (!Array.isArray(body.reasons)) {
      return { ok: false, error: 'reasons must be an array' };
    }
    const seen = new Set<RatingReason>();
    for (const r of body.reasons) {
      if (!isRatingReason(r)) {
        return { ok: false, error: `unknown reason: ${String(r)}` };
      }
      seen.add(r);
    }
    // Preserve the canonical RATING_REASONS order for stable, comparable ground truth.
    reasons = RATING_REASONS.filter((r) => seen.has(r));
  }

  return {
    ok: true,
    value: {
      gameId,
      category,
      prompt,
      response,
      verdict,
      ...(verdict === 'bad' ? { reasons: reasons ?? [] } : {}),
    },
  };
}

/**
 * Validate + normalize an untrusted clue-rating **retraction** (the "Undo" path). Reuses the
 * same authority as {@link validateClueRating} so the two can never drift: the (gameId,
 * category, prompt) key is trimmed and size-checked with the identical caps. A retraction
 * carries no verdict/response — it identifies the row to delete, nothing more. As with the
 * write, `ticket`/host authority is NOT this function's concern (enforced server-side).
 */
export function validateClueRatingRetraction(
  input: unknown,
): ClueRatingRetractionValidation {
  if (typeof input !== 'object' || input === null) {
    return { ok: false, error: 'retraction must be an object' };
  }
  const body = input as Record<string, unknown>;

  const gameId = typeof body.gameId === 'string' ? body.gameId.trim() : '';
  if (gameId.length === 0) return { ok: false, error: 'gameId is required' };
  if (gameId.length > MAX_RATING_GAME_ID_LEN) {
    return { ok: false, error: 'gameId is too long' };
  }

  const category = typeof body.category === 'string' ? body.category.trim() : '';
  const prompt = typeof body.prompt === 'string' ? body.prompt.trim() : '';
  if (category.length === 0 || prompt.length === 0) {
    return { ok: false, error: 'category and prompt are required' };
  }
  if (category.length > MAX_RATING_CATEGORY_LEN) {
    return { ok: false, error: 'category is too long' };
  }
  if (prompt.length > MAX_RATING_PROMPT_LEN) {
    return { ok: false, error: 'prompt is too long' };
  }

  return { ok: true, value: { gameId, category, prompt } };
}
